'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
{
  question: 'What happens after I submit the inquiry form?',
  answer: 'Within 24 hours, our team will review your submission and send a confirmation email with a calendar invite for your consultation. You\'ll also receive pre-consultation resources tailored to your objectives, including relevant case studies and methodology documentation.'
},
{
  question: 'Is there a cost for the initial consultation?',
  answer: 'No, the initial strategic consultation is complimentary for qualified enterprise prospects. This allows us to understand your needs and demonstrate how our AI orchestration approach can deliver measurable business outcomes for your organization.'
},
{
  question: 'Who should attend the consultation?',
  answer: 'We recommend including key decision-makers such as C-suite executives, IT leadership, and relevant business unit leaders. For technical deep-dives, including your CTO or IT Director is beneficial. We can accommodate multiple stakeholders in a single session.'
},
{
  question: 'What if I need to reschedule my consultation?',
  answer: 'We understand that executive schedules can change. Simply reply to your confirmation email or contact our team at least 24 hours before your scheduled time, and we\'ll work with you to find an alternative time that suits your calendar.'
},
{
  question: 'How do you ensure confidentiality during consultations?',
  answer: 'All consultations are conducted under strict confidentiality agreements. We\'re ISO 27001 certified and follow enterprise-grade security protocols. Any information shared during the consultation is protected and used solely to provide you with the best strategic guidance.'
},
{
  question: 'Can I request a specific consultation format?',
  answer: 'Absolutely. We offer in-person consultations (for Australia and New Zealand locations), virtual meetings, and executive briefing presentations. Select your preferred format in the inquiry form, and we\'ll accommodate your preference based on availability.'
}];


const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground">
              Common questions about our consultation process and partnership approach.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) =>
            <div
              key={index}
              className="bg-card rounded-lg border border-border overflow-hidden transition-all duration-300 hover:shadow-md">

                <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-200">

                  <span className="text-base font-display font-semibold text-foreground pr-4">
                    {faq.question}
                  </span>
                  <Icon
                  name="ChevronDownIcon"
                  size={20}
                  className={`flex-shrink-0 text-primary transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''}`
                  } />

                </button>
                
                <div
                className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96' : 'max-h-0'}`
                }>

                  <div className="px-6 pb-5 pt-2">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Have additional questions?
            </p>
            <a
              href="mailto:partnerships@mindshiftai.com"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-200">

              <Icon name="EnvelopeIcon" size={20} />
              <span className="font-medium">info@mindshiftai.co.nz</span>
            </a>
          </div>
        </div>
      </div>
    </section>);

};

export default FAQSection;